import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMaterialDetail, useUpdateMaterial } from "@/api/hooks/useCennik";
import { usePorezVysledok } from "@/api/hooks/usePorez";
import {
  useDeleteZakazkaCenaOverride,
  useUpsertZakazkaCenaOverride,
  useZakazkaCenaOverrides,
} from "@/api/hooks/useZakazkaCenaOverrides";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { formatEur, formatM2, formatPct, normalizeNum, parseDecimal, toNum } from "@/lib/format";
import { materialMaOdpad } from "@/lib/kategorie";

/**
 * Rýchla úprava cien materiálu/hrany/kovania priamo zo zákazky:
 * nákup, réžia %, práca €/MJ, odpad %.
 *
 * Default „Uložiť pre túto zákazku" — cena platí len v zákazke
 * (override), cenník ostáva nedotknutý. „Prepísať v cenníku" mení
 * cenu globálne (s potvrdením). Bez ``zakazkaId`` sa správa ako
 * globálna úprava cenníka. S ``variantaId`` ukazuje reálny odpad
 * z aktuálneho porezu (ten má v cene prednosť pred ručným %).
 */
export function MaterialCenaDialog({
  materialId,
  zakazkaId,
  variantaId,
  open,
  onOpenChange,
}: {
  materialId: string;
  zakazkaId?: string;
  variantaId?: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const detail = useMaterialDetail(materialId);
  const m = detail.data?.data ?? null;
  const updateMutation = useUpdateMaterial();
  const overridesQuery = useZakazkaCenaOverrides(zakazkaId);
  const upsertOverride = useUpsertZakazkaCenaOverride(zakazkaId ?? "");
  const deleteOverride = useDeleteZakazkaCenaOverride(zakazkaId ?? "");
  const override =
    (overridesQuery.data?.data ?? []).find(
      (o) => o.material_id === materialId,
    ) ?? null;

  // Reálny odpad, ktorý server dáva do ceny (KOLO 213): plán z Cut Rite,
  // inak aktuálny porez — má prednosť pred ručným %; pri paušáli nič
  const porez = usePorezVysledok(variantaId);
  const vCene = (porez.data?.data?.v_cene ?? []).find(
    (x) => x.material_id === materialId,
  );
  const porezItem =
    vCene?.zdroj === "porez"
      ? (porez.data?.data?.materialy ?? []).find(
          (pm) => pm.material.id === materialId,
        )
      : undefined;
  const cutriteItem =
    vCene?.zdroj === "cutrite"
      ? (porez.data?.data?.cutrite?.materialy ?? []).find(
          (cm) => cm.material_id === materialId,
        )
      : undefined;
  const porezOdpad = vCene?.odpad_pct;
  const zdrojOdpadu = cutriteItem ? "plánu Cut Rite" : "porezu";

  const [nakup, setNakup] = useState("");
  const [rezia, setRezia] = useState("");
  const [praca, setPraca] = useState("");
  const [nadmiera, setNadmiera] = useState("");
  useEffect(() => {
    if (m) {
      // Predvyplň efektívne hodnoty — override zákazky má prednosť
      setNakup(normalizeNum(override?.nakup_cena ?? m.nakup_cena));
      setRezia(normalizeNum(override?.rezia_pct ?? m.rezia_pct));
      setPraca(
        normalizeNum(override?.praca_eur_per_mj ?? m.praca_eur_per_mj),
      );
      setNadmiera(normalizeNum(override?.nadmiera_pct ?? m.nadmiera_pct));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m, override?.id]);

  // Odpad % má zmysel len pri doskách — hrany/kovanie ho v cene nenesú
  const maOdpad = materialMaOdpad(m?.druh, m?.kategoria, m?.sekcia);

  const nakupNum = toNum(nakup) || 0;
  const reziaNum = toNum(rezia) || 0;
  const pracaNum = toNum(praca) || 0;
  const materialCast = nakupNum * (1 + reziaNum / 100);
  const jednotkova = materialCast + pracaNum;
  // Efektívny odpad: aktuálny porez > ručná hodnota vo formulári
  const odpadNum =
    porezOdpad != null ? parseFloat(String(porezOdpad)) : toNum(nadmiera);
  const efektivnyOdpad = Number.isFinite(odpadNum) ? odpadNum : 0;
  // KOLO 19: klient platí prácu len na skutočných m² dielcov —
  // odpad aj zvyšky nesú výhradne materiál (nákup + réžia); prirážka
  // je strata z tabule (≤ 100 %)
  const cenaSOdpadom =
    materialCast * (1 + efektivnyOdpad / 100) + pracaNum;

  // Rozklad nadspotreby: € sa počíta z prirážok voči dielcom (interné),
  // ale PERCENTÁ zobrazujeme zo základne TABULE (dielce+zvyšky+odpad
  // = 100 %) — jediná intuitívna základňa (KOLO 18).
  const dielceM2 = parseFloat(String(porezItem?.plocha_dielcov_m2 ?? 0));
  const odpadM2 = parseFloat(String(porezItem?.odpad_m2 ?? 0));
  const zvyskyM2 = parseFloat(String(porezItem?.zvysky_m2 ?? 0));
  const tabuleM2 =
    porezItem != null
      ? (porezItem.pocet_tabul * porezItem.tabula_x_mm *
          porezItem.tabula_y_mm) /
        1_000_000
      : parseFloat(String(cutriteItem?.tabule_m2 ?? 0));
  const pocetTabul = porezItem?.pocet_tabul ?? cutriteItem?.tabule_ks ?? 0;
  const pctTabule = (m2: number): number =>
    tabuleM2 > 0 ? (m2 / tabuleM2) * 100 : 0;
  // rozklad prirážky (base tabuľa): odpad + neodpočítané zvyšky
  const odpadCastPct = pctTabule(odpadM2);
  const zvyskyCastPct = Math.max(0, efektivnyOdpad - odpadCastPct);

  async function ulozPreZakazku(): Promise<void> {
    try {
      await upsertOverride.mutateAsync({
        materialId,
        data: {
          nakup_cena: parseDecimal(nakup),
          rezia_pct: parseDecimal(rezia),
          praca_eur_per_mj: parseDecimal(praca),
          nadmiera_pct: maOdpad ? parseDecimal(nadmiera) || null : null,
        },
      });
      toast({
        title: "Cena uložená pre túto zákazku",
        description: `${m?.nazov ?? ""} — jednotková ${formatEur(jednotkova)}/${m?.mj ?? ""}. Cenník sa nemení.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Cenu sa nepodarilo uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  async function prepisVCenniku(): Promise<void> {
    // Upozornenie len v kontexte zákazky — tam je defaultom lokálna
    // úprava a globálny zápis je výnimočná akcia.
    if (
      zakazkaId &&
      !window.confirm(
        `Prepísať ceny materiálu „${m?.nazov ?? ""}" v CENNÍKU?\n\n` +
          "Zmena je globálna — prejaví sa vo VŠETKÝCH zákazkách, ktoré " +
          "tento materiál používajú (ceny sa počítajú živé z cenníka).",
      )
    ) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: materialId,
        data: {
          nakup_cena: parseDecimal(nakup),
          rezia_pct: parseDecimal(rezia),
          praca_eur_per_mj: parseDecimal(praca),
          ...(maOdpad && parseDecimal(nadmiera)
            ? { nadmiera_pct: parseDecimal(nadmiera) }
            : {}),
        },
      });
      // Override zákazky by novú cenníkovú cenu prekryl — zruš ho
      if (zakazkaId && override) {
        await deleteOverride.mutateAsync(materialId);
      }
      toast({
        title: "Ceny prepísané v cenníku",
        description: `${m?.nazov ?? ""} — jednotková ${formatEur(jednotkova)}/${m?.mj ?? ""}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Ceny sa nepodarilo uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  async function vratNaCennik(): Promise<void> {
    try {
      await deleteOverride.mutateAsync(materialId);
      toast({
        title: "Úprava zrušená",
        description: "Materiál sa v zákazke vrátil na cenníkovú cenu.",
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  const busy =
    updateMutation.isPending ||
    upsertOverride.isPending ||
    deleteOverride.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Upraviť ceny</DialogTitle>
          <DialogDescription className="sr-only">
            Rýchla úprava cien materiálu priamo zo zákazky — platí len pre túto zákazku.
          </DialogDescription>
        </DialogHeader>
        {m ? (
          <>
            <p className="min-w-0 truncate text-sm font-medium" title={m.nazov}>
              {m.nazov}
              {override ? (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-normal text-amber-800">
                  upravené pre zákazku
                </span>
              ) : null}
            </p>
            <div className={maOdpad ? "grid grid-cols-4 gap-3" : "grid grid-cols-3 gap-3"}>
              <div className="space-y-1">
                <Label htmlFor="mc-nakup" className="text-xs">
                  Nákup €/{m.mj}
                </Label>
                <Input
                  id="mc-nakup"
                  value={nakup}
                  onChange={(e) => setNakup(e.target.value)}
                  inputMode="decimal"
                  className="h-8 font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mc-rezia" className="text-xs">
                  Réžia %
                </Label>
                <Input
                  id="mc-rezia"
                  value={rezia}
                  onChange={(e) => setRezia(e.target.value)}
                  inputMode="decimal"
                  className="h-8 font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mc-praca" className="text-xs">
                  Práca €/{m.mj}
                </Label>
                <Input
                  id="mc-praca"
                  value={praca}
                  onChange={(e) => setPraca(e.target.value)}
                  inputMode="decimal"
                  className="h-8 font-mono"
                />
              </div>
              {maOdpad ? (
                <div className="space-y-1">
                  <Label
                    htmlFor="mc-nadmiera"
                    className="text-xs"
                    title="Odpad pri pílení % — násobí materiálovú zložku ceny. Plán z Cut Rite a aktuálny porez majú prednosť."
                  >
                    Odpad %
                  </Label>
                  <Input
                    id="mc-nadmiera"
                    value={nadmiera}
                    onChange={(e) => setNadmiera(e.target.value)}
                    inputMode="decimal"
                    className="h-8 font-mono"
                    disabled={porezOdpad != null}
                  />
                </div>
              ) : null}
            </div>
            {maOdpad && porezOdpad != null ? (
              <p
                className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-200"
                title={`Dielce ${formatM2(dielceM2)} m² · zvyšky ${formatM2(zvyskyM2)} m² · odpad ${formatM2(odpadM2)} m²`}
              >
                {cutriteItem ? "V cene platí plán z Cut Rite:" : "V cene platí aktuálny porez:"}{" "}
                <b>
                  {pocetTabul}{" "}
                  {pocetTabul === 1
                    ? "tabuľa"
                    : pocetTabul <= 4
                      ? "tabule"
                      : "tabúľ"}{" "}
                  ({formatM2(tabuleM2)} m²), strata{" "}
                  {formatPct(efektivnyOdpad, 1)}
                </b>
                . Ručný odpad % sa použije, až keď plán ani porez nebudú platiť
                (alebo v Rezaní zvolíš paušál).
              </p>
            ) : null}
            <div className="space-y-1 rounded-md bg-muted px-3 py-2 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground">
                  Výrobná cena
                  <span className="ml-1 text-[10px]">
                    (nákup × (1 + réžia) + práca)
                  </span>
                </span>
                <b className="font-mono">
                  {formatEur(jednotkova)}/{m.mj}
                </b>
              </div>
              {maOdpad ? (
                <div className="flex items-baseline justify-between border-t border-border pt-1">
                  <span
                    className="text-muted-foreground"
                    title={
                      porezOdpad != null
                        ? `Strata tabule = odpad ${formatPct(odpadCastPct, 1)} + zvyšky ${formatPct(zvyskyCastPct, 1)} — nesie len materiál s réžiou; práca sa účtuje na m² dielcov`
                        : "Ručná strata materiálu — nesie len nákup s réžiou; práca sa účtuje na m² dielcov"
                    }
                  >
                    Cena spolu
                    <span className="ml-1 text-[10px]">
                      (so stratou {formatPct(efektivnyOdpad)}
                      {porezOdpad != null ? ` z ${zdrojOdpadu}` : " ručne"})
                    </span>
                  </span>
                  <b className="font-mono text-primary">
                    {formatEur(cenaSOdpadom)}/{m.mj}
                  </b>
                </div>
              ) : null}
            </div>
            {zakazkaId ? (
              <p className="text-[11px] text-muted-foreground">
                „Uložiť pre túto zákazku" platí len tu — cenník sa nemení.
                „Prepísať v cenníku" zmení cenu globálne pre všetky zákazky.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Zmena sa uloží do cenníka a prejaví sa vo všetkých zákazkách —
                ceny dielcov sa prepočítavajú z aktuálneho cenníka.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Načítavam…</p>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {zakazkaId && override ? (
            <Button
              variant="ghost"
              className="mr-auto text-destructive hover:bg-destructive/10"
              onClick={() => void vratNaCennik()}
              disabled={busy}
              title="Zruší úpravu — materiál sa v zákazke vráti na cenníkovú cenu"
            >
              Vrátiť na cenník
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            title="Zavrieť bez uloženia"
          >
            Zrušiť
          </Button>
          {zakazkaId ? (
            <>
              <Button
                variant="outline"
                onClick={() => void prepisVCenniku()}
                disabled={!m || busy}
                title="Globálna zmena — prepíše ceny v cenníku pre všetky zákazky"
              >
                Prepísať v cenníku
              </Button>
              <Button
                onClick={() => void ulozPreZakazku()}
                disabled={!m || busy}
                title="Cena bude platiť len v tejto zákazke, cenník sa nemení"
              >
                Uložiť pre túto zákazku
              </Button>
            </>
          ) : (
            <Button
              onClick={() => void prepisVCenniku()}
              disabled={!m || busy}
              title="Uloží ceny do cenníka (platí globálne)"
            >
              Uložiť ceny
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
